"use client";

import React, { useEffect, useState } from "react";
import { useCart } from "@/src/contexts/CartContext";
import { toast } from "sonner";
import { HeroBannerProps } from "@/src/types/heroBanner";
import type { Product } from "@/src/types/tenant";
import { mapProduct } from "@/src/utils/products/mapProducts";
import { SafeImage } from "@/src/components/SafeImage";
import { VariantPickerModal } from "./VariantPickerModal";
import { ServiceAreaNotice } from "./ServiceAreaNotice";
import { useServiceArea } from "@/src/hooks/serviceArea/useServiceArea";

interface HeroProps extends HeroBannerProps {
  featuredProduct?: Product | null;
  outletId?: string;
  prefix?: string;
  ctaLabel?: string;
  /** The store's own badge line; replaces "Verified by GrabLocal · category · city". */
  badge?: string;
}

const inr = (n: number) => "₹" + n.toLocaleString("en-IN");

const Stars: React.FC<{ value: number; size?: number }> = ({ value, size = 16 }) => (
  <span className="inline-flex" style={{ gap: 1, color: "var(--gold)" }}>
    {Array.from({ length: 5 }).map((_, i) => (
      <svg key={i} width={size} height={size} viewBox="0 0 24 24" fill={i < Math.round(value) ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.6">
        <path d="M12 3l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.9 6.8 19.6l1-5.8-4.3-4.1 5.9-.9z" />
      </svg>
    ))}
  </span>
);

export const HeroBanner: React.FC<HeroProps> = ({
  title = "GrabLocal Store",
  subtitle = "Shop local favorites on GrabLocal",
  bannerImage,
  bannerImages,
  rating,
  ratingCount,
  city,
  category,
  minCartValue,
  featuredProduct,
  outletId = "",
  prefix = "",
  badge,
  ctaLabel,
}) => {
  const { addItem, getTotal, getItemQuantity } = useCart();
  const [adding, setAdding] = useState(false);
  const [showVariants, setShowVariants] = useState(false);
  const [slideIdx, setSlideIdx] = useState(0);
  const { area: serviceArea } = useServiceArea(outletId);

  // Resolve image list: bannerImages[] takes priority over single bannerImage.
  const slides = bannerImages?.length
    ? bannerImages
    : bannerImage
    ? [bannerImage]
    : [];

  // Auto-advance every 5 s when there are multiple slides.
  useEffect(() => {
    if (slides.length <= 1) return;
    const t = setInterval(() => setSlideIdx(i => (i + 1) % slides.length), 5000);
    return () => clearInterval(t);
  }, [slides.length]);

  const hasServiceArea = !!serviceArea;
  const displayRating = rating ? rating.toFixed(1) : null;
  const brief = subtitle.split("·")[0]?.trim();
  const locationLabel = [category, city].filter(Boolean).join(" · ") || subtitle.split("·")[1]?.trim();

  const fp = featuredProduct ? mapProduct(featuredProduct, outletId) : null;
  const fpHasImage = !!fp && fp.image.startsWith("http");
  const fpHasVariants = !!fp && (fp.variantCount ?? 0) >= 2;
  const fpInCart = !!fp && getItemQuantity(fp.id) > 0;
  const fpLow = !!fp && fp.stock !== undefined && fp.stock > 0 && fp.stock <= 5;

  const subtotal = getTotal();
  const threshold = minCartValue && minCartValue > 0 ? Number(minCartValue) : null;
  const unlocked = threshold != null && subtotal >= threshold;
  const remaining = threshold != null ? Math.max(0, threshold - subtotal) : 0;
  const progress = threshold != null ? Math.min(100, Math.round((subtotal / threshold) * 100)) : 0;

  const addFeatured = async () => {
    if (!fp) return;
    if (fpHasVariants) { setShowVariants(true); return; }
    if (!fp.tenantId) return;
    setAdding(true);
    try {
      await addItem({ productId: fp.id, outletId: fp.outletId, tenantId: fp.tenantId, name: fp.name, price: fp.price, mrp: fp.originalPrice, quantity: 1, image: fp.image });
      toast.success(`Added ${fp.name} to cart`);
    } catch (err) {
      toast.error((err as Error)?.message || "Couldn't add to cart");
    } finally {
      setAdding(false);
    }
  };

  return (
    <>
    <div className="mt-6 grid items-center gap-8 lg:gap-12 lg:grid-cols-[1.05fr_.95fr]">
      {/* ── Left: brand + proof + CTAs + nudge ── */}
      <div className="order-2 min-w-0 lg:order-1">
        {/* Delivery-area pill — renders only for outlets with a restriction.
            Those outlets show it *instead of* the verified badge. */}
        <ServiceAreaNotice outletId={outletId} />

        {!hasServiceArea && (
          <span className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[12px] font-bold" style={{ background: "var(--coral-soft)", color: "var(--coral-deep)", letterSpacing: "0.03em" }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M8.5 12l2.5 2.5 4.5-5" /></svg>
            {badge?.trim() || `Verified by GrabLocal${locationLabel ? ` · ${locationLabel}` : ""}`}
          </span>
        )}

        <h1 className="mt-4 leading-[1.04]" style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: "clamp(32px,5.2vw,58px)", letterSpacing: "-0.01em", color: "var(--techno-black)" }}>
          {title}
        </h1>
        {brief && <p className="mt-3 max-w-[460px]" style={{ fontSize: "clamp(15px,1.8vw,18px)", color: "var(--techno-black-70)", lineHeight: 1.55 }}>{brief}</p>}

        {displayRating && (
          <div className="mt-4 flex items-center gap-2.5">
            <Stars value={Number(rating)} size={17} />
            <span className="font-bold" style={{ fontSize: 15 }}>{displayRating}</span>
            {ratingCount != null && (
              <span style={{ color: "var(--techno-black-50)", fontSize: 14 }}>· {ratingCount.toLocaleString("en-IN")} rating{ratingCount === 1 ? "" : "s"}</span>
            )}
          </div>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <a
            href={`/shop/all-products?prefix=${prefix}`}
            className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-[15px] font-bold text-white transition hover:-translate-y-0.5"
            style={{ background: "var(--coral)", boxShadow: "0 10px 26px -10px rgba(242,98,80,0.7)" }}
          >
            {ctaLabel || "Shop the collection"}
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
          </a>
        </div>

        {threshold != null && (
          <div className="mt-6 max-w-[460px] rounded-2xl p-4" style={{ background: "var(--surface)", border: "1px solid var(--border-soft)", boxShadow: "var(--shadow-soft)" }}>
            <div className="flex items-center justify-between gap-3 text-[13px]">
              <span className="flex items-center gap-2 font-semibold" style={{ color: unlocked ? "var(--navy)" : "var(--techno-black)" }}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={unlocked ? "var(--navy)" : "var(--coral)"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1.5" y="6" width="12" height="10" rx="1.5" /><path d="M13.5 9H17l4 4v3h-7.5z" /><circle cx="6" cy="18.5" r="1.6" /><circle cx="18" cy="18.5" r="1.6" /></svg>
                {unlocked ? "Free delivery unlocked" : <>You&apos;re <strong style={{ color: "var(--coral-deep)" }}>{inr(remaining)}</strong> away from free delivery</>}
              </span>
              <span style={{ color: "var(--techno-black-50)", fontFamily: "var(--font-display)", fontWeight: 600 }}>{inr(subtotal)} / {inr(threshold)}</span>
            </div>
            <div className="mt-2.5 h-2 w-full overflow-hidden rounded-full" style={{ background: "var(--cream)" }}>
              <div className="h-full rounded-full transition-all" style={{ width: `${progress}%`, background: unlocked ? "var(--navy)" : "var(--coral)" }} />
            </div>
          </div>
        )}
      </div>

      {/* ── Right: banner carousel + floating featured product ── */}
      <div className="order-1 lg:order-2 relative">
        <span className="pointer-events-none absolute z-0" style={{ width: "70%", aspectRatio: "1", borderRadius: "50%", top: "-10%", right: "-6%", background: "radial-gradient(circle at 50% 70%, var(--coral-soft), transparent 65%)" }} />

        {/* Banner frame */}
        <div
          className="relative z-[1] overflow-hidden"
          style={{ height: "clamp(300px,34vw,470px)", borderRadius: "var(--radius-xl)", boxShadow: "var(--shadow-float)", background: "var(--navy)" }}
        >
          {slides.length > 0 ? (
            <>
              {/* Sliding track */}
              <div
                style={{
                  display: "flex",
                  width: `${slides.length * 100}%`,
                  height: "100%",
                  transform: `translateX(-${(slideIdx / slides.length) * 100}%)`,
                  transition: "transform 0.55s cubic-bezier(0.4,0,0.2,1)",
                }}
              >
                {slides.map((src, i) => (
                  <div key={i} style={{ width: `${100 / slides.length}%`, flexShrink: 0, height: "100%" }}>
                    <SafeImage
                      src={src}
                      alt={`${title} — slide ${i + 1}`}
                      loading={i === 0 ? "eager" : "lazy"}
                      fetchPriority={i === 0 ? "high" : undefined}
                      className="h-full w-full object-cover object-center"
                    />
                  </div>
                ))}
              </div>

              {/* Dot navigation — only when 2+ slides */}
              {slides.length > 1 && (
                <div
                  style={{
                    position: "absolute",
                    bottom: 14,
                    left: "50%",
                    transform: "translateX(-50%)",
                    display: "flex",
                    gap: 6,
                    zIndex: 4,
                  }}
                >
                  {slides.map((_, i) => (
                    <button
                      key={i}
                      aria-label={`Go to slide ${i + 1}`}
                      onClick={() => setSlideIdx(i)}
                      style={{
                        width: i === slideIdx ? 22 : 8,
                        height: 8,
                        borderRadius: 4,
                        background: i === slideIdx ? "#fff" : "rgba(255,255,255,0.45)",
                        border: "none",
                        cursor: "pointer",
                        padding: 0,
                        transition: "width 0.3s ease, background 0.3s ease",
                      }}
                    />
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="flex h-full w-full items-center justify-center" style={{ background: "radial-gradient(70% 80% at 72% 24%, rgba(242,98,80,0.5), transparent 60%), linear-gradient(150deg,#0E3358,#071F38)" }}>
              <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "clamp(80px,12vw,150px)", color: "rgba(255,255,255,0.18)" }}>{title.charAt(0).toUpperCase()}</span>
            </div>
          )}
        </div>

        {fp && (
          <div className="absolute bottom-4 left-4 right-4 z-[2] sm:right-auto sm:w-[330px]" style={{ background: "var(--surface)", border: "1px solid var(--border-soft)", borderRadius: "var(--radius-lg)", boxShadow: "var(--shadow-float)", padding: 12 }}>
            <div className="flex items-center gap-3">
              <div className="relative h-[58px] w-[58px] shrink-0 overflow-hidden" style={{ borderRadius: 12, background: "var(--cream)" }}>
                {fpHasImage ? (
                  <SafeImage src={fp.image} alt={fp.name} className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full items-center justify-center" style={{ fontFamily: "var(--font-display)", color: "rgba(9,42,76,0.22)", fontSize: 24 }}>{fp.name.slice(0, 1).toUpperCase()}</div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <span className="inline-block rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.06em]" style={{ background: "var(--navy)", color: "#fff" }}>Bestseller</span>
                <h3 className="mt-1 truncate" style={{ fontWeight: 600, fontSize: 13.5, color: "var(--techno-black)" }}>{fp.name}</h3>
                <div className="flex items-baseline gap-1.5">
                  <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 16, color: "var(--techno-black)" }}>{inr(fp.price)}</span>
                  {fp.originalPrice && <s className="text-[11px]" style={{ color: "var(--techno-black-50)" }}>{inr(fp.originalPrice)}</s>}
                  {fp.discountPct != null && fp.discountPct >= 5 && (
                    <span className="text-[10.5px] font-bold" style={{ color: "var(--coral-deep)" }}>{fp.discountPct}% off</span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={addFeatured}
              disabled={adding}
              className="mt-2.5 flex h-10 w-full items-center justify-center gap-2 rounded-full text-[13.5px] font-bold text-white transition active:scale-[0.98] disabled:opacity-60"
              style={{ background: "var(--coral)" }}
            >
              {adding ? "Adding…" : fpHasVariants ? "Choose options" : fpInCart ? "Add another" : "Add to cart"}
              {!adding && (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" /><path d="M3 6h18" /><path d="M16 10a4 4 0 01-8 0" /></svg>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
    {showVariants && fp && <VariantPickerModal product={fp} onClose={() => setShowVariants(false)} />}
    </>
  );
};
